/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { createSelector, createSlice } from '@reduxjs/toolkit'

import type { PayloadAction } from '@reduxjs/toolkit'
import { appApi } from 'slices/api'
import type { RootState } from 'store' // Adjust the import path to where your RootState is defined
import { type PortalChildDetails, type ChildEditValues, type caregiverChildRelationship } from 'core/types'
import { type DuplicateStats } from 'Components/DuplicateHandling/RecentlyCreatedDuplicates'
import { type DuplicateItem } from 'Components/DuplicateHandling/DuplicateTable'
import { type SiblingEditModel } from 'Components/CareNetwork/SiblingEdit'

export interface ChildDuplicate {
  id?: number
  childId?: number
  firstName: string | null
  lastName: string
  nickName?: string
  suffix?: string
  middleName?: string
  birthGender: string
  race: string
  languageID: string
  address: string
  city: string
  state: string
  zipCode: string
  apartment: string
  dateOfBirth?: string
  dueDate?: string
  eligibilityFactors?: string
  otherEligibilityFactor?: string
  additionalFactors?: string
  existingLocations?: string
  siblingIds?: string
  exiting?: boolean
  exitDate?: Date
  exitReason?: string
  matchedValues?: MatchValue[]
  environmentalRisk?: string
  tribalAffiliationStatus?: boolean
  tribalAffiliationID?: number
  hasIEP?: boolean
  hasIFSP?: boolean
  hasEOIS?: boolean
  isdId: number
  canAccess: boolean
  hasSNAPBenefits?: boolean
  isFosterChild?: boolean
  isOnCashAssistance?: boolean
  isOnSSI?: boolean
  authorizationStatusID?: number
  isImmigrant?: boolean
  iepRecommendsInclusivePlacement?: boolean
  reasonEndingID?: number
  reasonEndingComment?: string
  exitReasonID?: number
  enrollmentISDID?: string | null
  serviceID?: string | null
  programID?: string | null
  siblingId?: string | null
  schoolYearID?: string | null
  isHomeless?: boolean
  startDate?: Date
  endDate?: Date
  homelessResidenceID?: number
  isUnaccompaniedYouth?: boolean
  requestingUserID: string
  requestingUserFirstName?: string
  requestingUserLastName?: string
  requestingUserPhone?: string
  requestingUserEmail?: string
  requestingUserRelationship?: string
  requestingUserIsFinanciallyResponsible?: boolean
  requestingUserLivesWithChild?: boolean
  requestingUserPrimaryLanguage?: string
  requestingUserDob?: Date
  requestingUserAddress?: string
  requestingUserCity?: string
  requestingUserState?: string
  requestingUserZip?: string
  requestingUserS3Key?: string
  requestingUserIsCaregiver: boolean
  requestingUserISDName?: string
  requestingUserSecurityLevel?: string
  probability?: number
  source: duplicateSource
  notes?: string | null
  caregiverRelationships: caregiverChildRelationship[]
}
export interface MatchValue {
  type: 'DOB' | 'First Name' | 'Last Name' | 'Gender' | 'Address' | 'UIC'
  strength: 'Exact' | 'Partial' | 'No'
  points: number
}
export interface enrollmentForexistingChild {
  isdId: number
  childId: number
  providerId: number
  serviceId: string
  programId: string
  schoolYearId: string
}
export interface duplicateResolution {
  reasoning: string
  duplicateId: number | null
  duplicate: ChildDuplicate | null
  matchedChildID: number
  childID: number | null
  caregiverID: number
  providerID: number | null
  matchedChildNickName: string | null
  sessionKey?: string | null
  isdId?: string | null
  source: duplicateSource
  notes?: string | null
}

export enum duplicateSource {
  ChildEntry = 'duplicate/CreateDuplicateFromChildEntry',
  ChildEdit = 'duplicate/CreateDuplicateFromChildEdit',
  CaregiverPortal = 'duplicate/CreateDuplicateFromCaregiverPortal',
  Sibling = 'duplicate/CreateDuplicateFromSibling'
}
export interface dupeCommonValues {
  id?: number
  matchedChildId: number
  firstName: string
  lastName: string
  birthGender: string
  address?: string
  city?: string
  state?: string
  zipCode?: string
  apartment?: string
  dateOfBirth: string
  requestingISDID: number
  race?: string
  language?: string
  nickName?: string
  matchPercent?: number
  matchedValues: MatchValue[]

}
export interface duplicateState {
  resolved: boolean
  originalChildren: ChildDuplicate[]
  duplicateChild: ChildDuplicate | null
  wasChildCreated: boolean | null
  reasoning: string | null
  grantAccess: boolean
  portalView: boolean
  portalModify: boolean
}
const initialState: duplicateState = {
  duplicateChild: null,
  wasChildCreated: null,
  reasoning: null,
  originalChildren: [],
  resolved: false,
  grantAccess: false,
  portalView: false,
  portalModify: false
}
/// This is the collection of api endpoints used for the child duplicate process.
/// check the 'extra reducers' for a matcher that will fire after the api successfully completes an action, and use that to update the state.
/// This isnt exactly ideal as far as caching goes for the queries, since you're basically storing the call results in both the api and the slice state, but we want to
/// be able to manipulate this data with our logic in the state slice. If we are doing something that isnt manipulating anything, we can use the tags collection as expected
const duplicateChildApi = appApi.injectEndpoints({
  endpoints: (builder) => ({
    checkChildDuplicate: builder.query<ChildDuplicate[], ChildDuplicate>({
      query: (child) => ({
        url: 'Duplicate/CheckDuplicates',
        method: 'post',
        credentials: 'include',
        body: {
          id: child.childId,
          firstName: child.firstName,
          lastName: child.lastName,
          birthGender: child.birthGender,
          dateOfBirth: child.dateOfBirth,
          dueDate: child.dueDate,
          address: child.address,
          apartment: child.apartment,
          city: child.city,
          state: child.state,
          zipCode: child.zipCode
        }
      })
    }),
    getAllDuplicates: builder.query<DuplicateItem[], { newOnly: boolean, unresolvedOnly: boolean }>({
      query: ({ newOnly, unresolvedOnly }) => ({
        url: 'Duplicate/GetAllDuplicates',
        method: 'post',
        credentials: 'include',
        body: { newOnly, unresolvedOnly }
      }),
      providesTags: (result = [], error, arg) => ['Duplicates', ...result.map((r) => ({ type: 'Duplicates', id: r.duplicateID }) as const)]
    }),
    getDuplicateStats: builder.query<DuplicateStats, void | never>({
      query: () => ({
        url: 'Duplicate/GetDuplicateStats',
        method: 'post',
        credentials: 'include'
      })
    }),
    loadChildDuplicate: builder.query<duplicateState, number>({
      query: (childId) => ({
        url: `Duplicate/LoadDuplicate?id=${childId}`,
        method: 'get',
        credentials: 'include'
      })
    }),
    declineDuplicate: builder.mutation<boolean, { resolution: duplicateResolution, dupeState: duplicateState | null }>({
      query: ({ resolution: dupeResolution, dupeState }) => ({
        url: 'duplicate/declineDuplicate',
        method: 'post',
        credentials: 'include',
        body: {
          reasoning: dupeResolution.reasoning,
          duplicateId: dupeResolution.duplicateId,
          matchedChildId: dupeResolution.matchedChildID,
          caregiverid: dupeResolution.caregiverID,
          grantAccess: dupeState?.grantAccess,
          portalView: dupeState?.portalView,
          portalModify: dupeState?.portalModify
        }
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'Duplicates', id: arg.resolution.duplicateId ?? 0 }]
    }),
    createDuplicate: builder.mutation<number, duplicateResolution>({
      query: (dupeResolution) => ({
        url: dupeResolution.source,
        method: 'post',
        credentials: 'include',
        body: {
          reasoning: dupeResolution.reasoning,
          childID: dupeResolution.childID,
          matchedChildID: dupeResolution.matchedChildID,
          caregiverID: dupeResolution.caregiverID,
          isdSPID: dupeResolution.providerID,
          isdID: dupeResolution.isdId,
          duplicateId: null,
          matchedChildNickName: dupeResolution.matchedChildNickName,
          duplicateModel: dupeResolution.duplicate,
          sessionKey: dupeResolution.sessionKey,
          notes: dupeResolution.notes
        }
      }),
      invalidatesTags: ['Duplicates']

    }),
    approveDuplicate: builder.mutation<number, duplicateResolution>({
      query: (dupeResolution) => ({
        url: 'duplicate/approveDuplicate',
        method: 'post',
        credentials: 'include',
        body: {
          reasoning: dupeResolution.reasoning,
          duplicateId: dupeResolution.duplicateId,
          matchedChildId: dupeResolution.matchedChildID,
          caregiverid: dupeResolution.caregiverID,
          source: dupeResolution.source
        }
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'Duplicates', id: arg.duplicateId ?? 0 }]

    }),
    getMyDuplicates: builder.query<DuplicateItem[], void | never>({
      query: () => ({
        url: 'Duplicate/GetMyDuplicates',
        method: 'post',
        credentials: 'include'
      }),
      providesTags: (result = [], error, arg) => ['Duplicates', ...result.map((r) => ({ type: 'Duplicates', id: r.duplicateID }) as const)]
    })
  }),
  overrideExisting: false
})
/// This is the state for the child duplicate process.
export const childDuplicate = createSlice({
  name: 'childDuplicate',
  initialState,
  reducers: {
    clear: (state) => {
      state.originalChildren = []
      state.duplicateChild = null
      state.resolved = false
      state.wasChildCreated = null
      state.reasoning = null
      state.grantAccess = false
      state.portalView = false
      state.portalModify = false
    },
    updateDuplicateInfo: (state, action: PayloadAction<ChildDuplicate>) => {
      state.duplicateChild = action.payload
    },
    updateAccessPermissions: (state, action: PayloadAction<{ grantAccess: boolean, portalView: boolean, portalModify: boolean }>) => {
      state.grantAccess = action.payload.grantAccess
      state.portalView = action.payload.portalView
      state.portalModify = action.payload.portalModify
    }
  },
  extraReducers: (builder) => {
    builder.addMatcher(duplicateChildApi.endpoints.checkChildDuplicate.matchFulfilled, (state, action) => {
      state.originalChildren = action.payload
    })
    builder.addMatcher(duplicateChildApi.endpoints.loadChildDuplicate.matchFulfilled, (state, action) => {
      state.resolved = action.payload.resolved
      state.originalChildren = action.payload.originalChildren
      state.duplicateChild = action.payload.duplicateChild
      state.reasoning = action.payload.reasoning
      state.wasChildCreated = action.payload.wasChildCreated
    })
  }
})
export const translatePortalChild = (data: PortalChildDetails, isdId: string): ChildDuplicate => {
  return {
    ...data,
    dateOfBirth: data.unborn ? null : new Date(data.dateOfBirth?.toString() ?? '').toDateString(),
    dueDate: data.unborn ? new Date(data.dueDate?.toString() ?? '').toDateString() : null,
    /**
     isHomeless: boolean
    dueDate?: Date | null
    unborn: boolean
    livesWithFirstChild?: boolean
    requestedServices?: PortalService[] */
    isFosterChild: data.fosterChild,
    address: data.location.address,
    city: data.location.city,
    state: data.location.state,
    zipCode: data.location.zipCode,
    apartment: data.location.apartment,
    race: data.races?.join(','),
    languageID: data.languageID?.toString(),
    isdId: Number.parseInt(isdId),
    canAccess: true,
    enrollmentISDID: null,
    matchedValues: [],
    requestingUserID: '',
    requestingUserIsCaregiver: false,
    source: duplicateSource.CaregiverPortal,
    caregiverRelationships: []

  } as ChildDuplicate
}
export const translateChildEditValues = (data: ChildEditValues, source: duplicateSource, params?: URLSearchParams | null): ChildDuplicate => {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const enrollmentISDID = params?.get('isdID') ?? null
  const serviceID = params?.get('serviceID')
  const programID = params?.get('programID')
  const schoolYearID = params?.get('schoolYearID')
  const requestingUserID = params?.get('requestingUserID')
  const firstLoc = (data.childLocations != null && data.childLocations.length > 0) ? data.childLocations[0] : null
  const dupeDat: ChildDuplicate = {
    ...data,
    ID: null,
    childId: data.id,
    dateOfBirth: data.dateOfBirth != null && data.dateOfBirth.toString() !== 'Invalid Date' ? new Date(data.dateOfBirth?.toString() ?? '').toDateString() : '',
    dueDate: data.dueDate != null && data.dueDate.toString() !== 'Invalid Date' ? new Date(data.dueDate?.toString() ?? '').toDateString() : '',
    languageID: data.languageID?.toString(),
    image: '',
    canAccess: true,
    race: data.childRaceList.map(r => r.name).join(','),
    eligibilityFactors: data.eligibilityFactorList.map(e => e.name).join(','),
    environmentalRisk: data.environmentalRiskList.map(e => e.name).join(','),
    isdId: Number.parseInt(enrollmentISDID ?? '0'),
    address: firstLoc?.address,
    apartment: firstLoc?.apartment,
    city: firstLoc?.city,
    state: firstLoc?.state,
    zipCode: firstLoc?.zipCode,
    isHomeless: firstLoc?.isHomeless,
    startDate: firstLoc?.startDate,
    endDate: firstLoc?.endDate,
    homelessResidenceID: firstLoc?.homelessResidenceID,
    isUnaccompaniedYouth: firstLoc?.isUnaccompaniedYouth,
    matchedValues: [],
    enrollmentISDID,
    serviceID,
    programID,
    schoolYearID,
    requestingUserID,
    requestingUserIsCaregiver: false,
    source,
    caregiverRelationships: []
  } as ChildDuplicate
  return dupeDat
}
export const translateSiblingEditValues = (data: SiblingEditModel, source: duplicateSource, params?: URLSearchParams | null): ChildDuplicate => {
  const dat = translateChildEditValues(data, source, params)
  if (data.livesWithChild) {
    dat.existingLocations = data.primaryLocationID?.toString()
    dat.address = data.address ?? ''
    dat.zipCode = data.zipCode ?? ''
    dat.city = data.city ?? ''
    dat.state = data.state ?? ''
    dat.apartment = data.apartment ?? ''
  }

  return dat
}

export default childDuplicate.reducer
export const { updateDuplicateInfo, clear, updateAccessPermissions } = childDuplicate.actions

export const duplicateStateSelector = (state: RootState): duplicateState | null => state.childDuplicate
export const dupeSelector = (state: RootState): ChildDuplicate | null => state.childDuplicate.duplicateChild
export const originalSelector = (state: RootState): ChildDuplicate[] => state.childDuplicate.originalChildren

export const firstNameSelector = createSelector(dupeSelector, dupe => dupe?.firstName)
export const {
  useLazyCheckChildDuplicateQuery,
  useCreateDuplicateMutation,
  useDeclineDuplicateMutation, useApproveDuplicateMutation,
  useLazyLoadChildDuplicateQuery, useGetAllDuplicatesQuery,
  useGetDuplicateStatsQuery,
  useGetMyDuplicatesQuery
} = duplicateChildApi
